import * as d3 from 'd3';

/**
 * Statistics visualization panel
 */
export class Statistics {
  constructor(selector, dataProcessor) {
    this.container = d3.select(selector);
    this.dataProcessor = dataProcessor;
    this.listeners = {};

    this.init();
  }

  init() {
    // Create container for line chart
    this.timelineChartContainer = this.container.append('div')
      .attr('class', 'stat-chart');

    this.timelineChartContainer.append('h3').text('Locations Over Time');

    this.timelineSvg = this.timelineChartContainer.append('svg')
      .attr('width', '100%')
      .attr('height', 180);

    // Create container for location types bar chart
    this.typeChartContainer = this.container.append('div')
      .attr('class', 'stat-chart');

    this.typeChartContainer.append('h3').text('Location Types');

    this.typeSvg = this.typeChartContainer.append('svg')
      .attr('width', '100%')
      .attr('height', 250);

    // Get year range for initial setup
    const yearRange = this.dataProcessor.getYearRange();
    this.yearRange = yearRange;
    this.currentYear = yearRange[0];
    this.windowSize = 10;

    // Render initial charts
    this.renderTimelineChart();

    // Initial render of type breakdown (will be updated via update() call)
    this.renderTypeBreakdownChart(yearRange[0], yearRange[0] + this.windowSize);
  }

  renderTimelineChart() {
    const data = this.dataProcessor.getLocationCountsOverTime();

    const margin = { top: 10, right: 10, bottom: 30, left: 35 };
    const containerWidth = this.timelineChartContainer.node().clientWidth;
    const width = containerWidth - margin.left - margin.right;
    const height = 180 - margin.top - margin.bottom;

    // Store dimensions for brush updates
    this.timelineWidth = width;
    this.timelineHeight = height;
    this.timelineMargin = margin;

    const g = this.timelineSvg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create scales
    this.xScale = d3.scaleLinear()
      .domain(d3.extent(data, d => d.year))
      .range([0, width]);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => Math.max(d.metadata1, d.metadata2))])
      .range([height, 0]);

    // Create line generators
    const line1 = d3.line()
      .x(d => this.xScale(d.year))
      .y(d => y(d.metadata1));

    const line2 = d3.line()
      .x(d => this.xScale(d.year))
      .y(d => y(d.metadata2));

    // Draw axes with styling
    g.append('g')
      .attr('class', 'stats-axis')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(this.xScale).tickFormat(d3.format('d')).ticks(4))
      .selectAll('text')
      .style('font-size', '10px');

    g.append('g')
      .attr('class', 'stats-axis')
      .call(d3.axisLeft(y).ticks(4))
      .selectAll('text')
      .style('font-size', '10px');

    // Draw lines
    g.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#ff6464')
      .attr('stroke-width', 2)
      .attr('d', line1);

    g.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#6496ff')
      .attr('stroke-width', 2)
      .attr('d', line2);

    // Add brush for time window selection
    this.brush = d3.brushX()
      .extent([[0, 0], [width, height]])
      .on('brush end', (event) => this.onBrush(event));

    this.brushGroup = g.append('g')
      .attr('class', 'timeline-brush')
      .call(this.brush);

    // Set initial brush position
    this.updateBrushPosition();

    // Add compact legend
    const legend = g.append('g')
      .attr('transform', `translate(5, 5)`);

    legend.append('line')
      .attr('x1', 0)
      .attr('x2', 15)
      .attr('y1', 0)
      .attr('y2', 0)
      .attr('stroke', '#ff6464')
      .attr('stroke-width', 2);

    legend.append('text')
      .attr('x', 18)
      .attr('y', 4)
      .attr('fill', '#e0e0e0')
      .style('font-size', '9px')
      .text('JDC');

    legend.append('line')
      .attr('x1', 55)
      .attr('x2', 70)
      .attr('y1', 0)
      .attr('y2', 0)
      .attr('stroke', '#6496ff')
      .attr('stroke-width', 2);

    legend.append('text')
      .attr('x', 73)
      .attr('y', 4)
      .attr('fill', '#e0e0e0')
      .style('font-size', '9px')
      .text('SMDB');
  }

  updateBrushPosition() {
    if (!this.brushGroup || !this.xScale) return;

    const start = this.xScale(this.currentYear);
    const end = this.xScale(this.currentYear + this.windowSize);
    this.brushGroup.call(this.brush.move, [start, end]);
  }

  onBrush(event) {
    if (!event.selection) return;

    const [x0, x1] = event.selection;
    const year0 = Math.round(this.xScale.invert(x0));
    const year1 = Math.round(this.xScale.invert(x1));

    const yearChanged = year0 !== this.currentYear;
    const sizeChanged = (year1 - year0) !== this.windowSize;

    this.currentYear = year0;
    this.windowSize = year1 - year0;

    // Emit change event to update the app
    if (yearChanged || sizeChanged) {
      this.emit('change', { year: this.currentYear, windowSize: this.windowSize });
    }
  }

  setYear(year) {
    this.currentYear = Math.max(
      this.yearRange[0],
      Math.min(year, this.yearRange[1] - this.windowSize)
    );
    this.updateBrushPosition();
  }

  setWindowSize(size) {
    this.windowSize = size;
    this.updateBrushPosition();
  }

  on(event, callback) {
    this.listeners[event] = callback;
  }

  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event](data);
    }
  }

  renderTypeBreakdownChart(windowStart, windowEnd) {
    // Clear previous chart
    this.typeSvg.selectAll('*').remove();

    const typeBreakdown = this.dataProcessor.getLocationTypeBreakdown(windowStart, windowEnd);

    // Combine all types from both datasets
    const allTypes = new Set([
      ...Object.keys(typeBreakdown.metadata1),
      ...Object.keys(typeBreakdown.metadata2)
    ]);

    // Convert to array of objects for D3
    const data = Array.from(allTypes).map(type => ({
      type,
      metadata1: typeBreakdown.metadata1[type] || 0,
      metadata2: typeBreakdown.metadata2[type] || 0
    }));

    // Sort by total count (descending)
    data.sort((a, b) => (b.metadata1 + b.metadata2) - (a.metadata1 + a.metadata2));

    // Take top 10 to keep chart readable
    const topData = data.slice(0, 10);

    if (topData.length === 0) {
      this.typeSvg.append('text')
        .attr('x', 150)
        .attr('y', 125)
        .attr('text-anchor', 'middle')
        .attr('fill', '#999')
        .text('No data for this period');
      return;
    }

    const margin = { top: 10, right: 10, bottom: 60, left: 50 };
    const containerWidth = this.typeChartContainer.node().clientWidth;
    const width = containerWidth - margin.left - margin.right;
    const height = 250 - margin.top - margin.bottom;

    const g = this.typeSvg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create scales
    const x0 = d3.scaleBand()
      .domain(topData.map(d => d.type))
      .range([0, width])
      .padding(0.2);

    const x1 = d3.scaleBand()
      .domain(['metadata1', 'metadata2'])
      .range([0, x0.bandwidth()])
      .padding(0.05);

    const y = d3.scaleLinear()
      .domain([0, d3.max(topData, d => Math.max(d.metadata1, d.metadata2))])
      .nice()
      .range([height, 0]);

    // Draw axes
    g.append('g')
      .attr('class', 'stats-axis')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x0))
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end')
      .style('font-size', '9px');

    g.append('g')
      .attr('class', 'stats-axis')
      .call(d3.axisLeft(y).ticks(5))
      .selectAll('text')
      .style('font-size', '10px');

    // Draw bars for each type
    const typeGroups = g.selectAll('.type-group')
      .data(topData)
      .join('g')
      .attr('class', 'type-group')
      .attr('transform', d => `translate(${x0(d.type)},0)`);

    // Metadata 1 bars
    typeGroups.append('rect')
      .attr('x', x1('metadata1'))
      .attr('y', d => y(d.metadata1))
      .attr('width', x1.bandwidth())
      .attr('height', d => height - y(d.metadata1))
      .attr('fill', '#ff6464')
      .attr('opacity', 0.8);

    // Metadata 2 bars
    typeGroups.append('rect')
      .attr('x', x1('metadata2'))
      .attr('y', d => y(d.metadata2))
      .attr('width', x1.bandwidth())
      .attr('height', d => height - y(d.metadata2))
      .attr('fill', '#6496ff')
      .attr('opacity', 0.8);

    // Add legend
    const legend = g.append('g')
      .attr('transform', `translate(5, 5)`);

    legend.append('rect')
      .attr('width', 12)
      .attr('height', 12)
      .attr('fill', '#ff6464');

    legend.append('text')
      .attr('x', 16)
      .attr('y', 10)
      .attr('fill', '#e0e0e0')
      .style('font-size', '9px')
      .text('JDC');

    legend.append('rect')
      .attr('x', 55)
      .attr('width', 12)
      .attr('height', 12)
      .attr('fill', '#6496ff');

    legend.append('text')
      .attr('x', 71)
      .attr('y', 10)
      .attr('fill', '#e0e0e0')
      .style('font-size', '9px')
      .text('SMDB');
  }

  update(windowStart, windowEnd) {
    // Render location type breakdown chart
    this.renderTypeBreakdownChart(windowStart, windowEnd);
  }
}
